import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {isAxiosError} from 'axios'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'

export type PerfilSociodemografico = components['schemas']['PerfilRead']
export type PerfilSociodemograficoUpdate = components['schemas']['PerfilUpdate']
export type PerfilSociodemograficoVersion = components['schemas']['PerfilVersionSummary']

const KEY = 'mi-perfil-sociodemografico'

/** El wizard edita todo como string. Vive acá y no en la página porque `components/` no puede importar de
 * `pages/` (dependency-cruiser). */
export type Draft = {
  fecha_ingreso: string
  tipo_contrato: string
  modalidad: string
  transporte_principal: string
  visita_clientes: string
  minutos_desplazamiento: string
  estado_civil: string
  numero_hijos: string
  personas_a_cargo: string
  integrantes_hogar: string
  tipo_vivienda: string
  tipo_sangre: string
  contacto_emergencia_nombre: string
  contacto_emergencia_parentesco: string
  contacto_emergencia_telefono: string
  enfermedad_diagnosticada: string
  enfermedad_detalle: string
  fuma: string
  fuma_frecuencia: string
  alcohol: string
  alcohol_frecuencia: string
  sustancias_psicoactivas: string
  ejercicio: string
  ejercicio_frecuencia: string
  ejercicio_deporte: string
  talla_cm: string
  peso_kg: string
}

function boolToStr(value: boolean | null | undefined): string {
  if (value === true) return 'true'
  if (value === false) return 'false'
  return ''
}

export function strToBool(value: string): boolean | null {
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

export function draftFromPerfil(perfil: PerfilSociodemografico): Draft {
  return {
    fecha_ingreso: perfil.fecha_ingreso ?? '',
    tipo_contrato: perfil.tipo_contrato ?? '',
    modalidad: perfil.modalidad ?? '',
    transporte_principal: perfil.transporte_principal ?? '',
    visita_clientes: boolToStr(perfil.visita_clientes),
    minutos_desplazamiento: perfil.minutos_desplazamiento?.toString() ?? '',
    estado_civil: perfil.estado_civil ?? '',
    numero_hijos: perfil.numero_hijos?.toString() ?? '',
    personas_a_cargo: perfil.personas_a_cargo?.toString() ?? '',
    integrantes_hogar: perfil.integrantes_hogar?.toString() ?? '',
    tipo_vivienda: perfil.tipo_vivienda ?? '',
    tipo_sangre: perfil.tipo_sangre ?? '',
    contacto_emergencia_nombre: perfil.contacto_emergencia_nombre ?? '',
    contacto_emergencia_parentesco: perfil.contacto_emergencia_parentesco ?? '',
    contacto_emergencia_telefono: perfil.contacto_emergencia_telefono ?? '',
    enfermedad_diagnosticada: boolToStr(perfil.enfermedad_diagnosticada),
    enfermedad_detalle: perfil.enfermedad_detalle ?? '',
    fuma: boolToStr(perfil.fuma),
    fuma_frecuencia: perfil.fuma_frecuencia ?? '',
    alcohol: boolToStr(perfil.alcohol),
    alcohol_frecuencia: perfil.alcohol_frecuencia ?? '',
    sustancias_psicoactivas: boolToStr(perfil.sustancias_psicoactivas),
    ejercicio: boolToStr(perfil.ejercicio),
    ejercicio_frecuencia: perfil.ejercicio_frecuencia ?? '',
    ejercicio_deporte: perfil.ejercicio_deporte ?? '',
    talla_cm: perfil.talla_cm?.toString() ?? '',
    peso_kg: perfil.peso_kg?.toString() ?? '',
  }
}

export function useMyPerfilSociodemografico(businessId: string | undefined) {
  return useQuery({
    queryKey: [KEY, businessId],
    queryFn: async () => (await api.get<PerfilSociodemografico>(`/businesses/${businessId}/mi/perfil`)).data,
    enabled: !!businessId,
  })
}

/** Literal exacto del 409 de `routers/mi_perfil.py`; la misma cadena vive en `lib/errors.ts`. */
const PERFIL_YA_SELLADO = 'the perfil is already sealed'

export function esPerfilYaSellado(err: unknown): boolean {
  return (
    isAxiosError(err) &&
    err.response?.status === 409 &&
    (err.response.data as {detail?: unknown} | undefined)?.detail === PERFIL_YA_SELLADO
  )
}

/** Un 409 «ya sellado» no es un fallo: se responde con el perfil tal cual para resolver en éxito (PYTHON-3P). */
async function oPerfilSellado(err: unknown, businessId: string): Promise<PerfilSociodemografico> {
  if (!esPerfilYaSellado(err)) throw err
  return (await api.get<PerfilSociodemografico>(`/businesses/${businessId}/mi/perfil`)).data
}

/** Partial PUT per answer (spec D3); 409 before consent, and once sealed the 409 resolves as the sealed perfil. */
export function useUpdateMyPerfilSociodemografico(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PerfilSociodemograficoUpdate) => {
      try {
        return (await api.put<PerfilSociodemografico>(`/businesses/${businessId}/mi/perfil`, payload)).data
      } catch (err) {
        return oPerfilSellado(err, businessId)
      }
    },
    onSuccess: (data) => queryClient.setQueryData([KEY, businessId], data),
  })
}

export function useConsentMyPerfilSociodemografico(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<PerfilSociodemografico>(`/businesses/${businessId}/mi/perfil/consentimiento`, {})).data,
    onSuccess: (data) => queryClient.setQueryData([KEY, businessId], data),
  })
}

/** Sellar dos veces resuelve en el perfil sellado. Invalida el onboarding porque `perfil_sellado_vigente` es
 * lo que cierra el registro (KAI-219). */
export function useSealMyPerfilSociodemografico(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      try {
        return (await api.post<PerfilSociodemografico>(`/businesses/${businessId}/mi/perfil/sellar`, {})).data
      } catch (err) {
        return oPerfilSellado(err, businessId)
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData([KEY, businessId], data)
      void queryClient.invalidateQueries({queryKey: ['mi-onboarding', businessId]})
    },
  })
}

/** R6: una o dos versiones por año y ninguna se borra; la página 1 es lo vigente. */
const PERFIL_HISTORIAL_PAGE_SIZE = 10

export function useMyPerfilSociodemograficoHistorial(businessId: string | undefined, page = 1) {
  return usePagedList<PerfilSociodemograficoVersion>(
    [KEY, 'historial', businessId],
    businessId ? `/businesses/${businessId}/mi/perfil/historial` : undefined,
    {page, search: ''},
    PERFIL_HISTORIAL_PAGE_SIZE
  )
}

export function useMyPerfilSociodemograficoVersion(businessId: string | undefined, perfilId: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'historial', businessId, perfilId],
    queryFn: async () =>
      (await api.get<PerfilSociodemografico>(`/businesses/${businessId}/mi/perfil/historial/${perfilId}`)).data,
    enabled: !!businessId && !!perfilId,
  })
}
